<template>
  <!--CAMPO PARA GUARDAR VARIABLE-->
  <a-form-item
    class="picker"
    :required="formState.checked"
    :validate-status="hasError ? 'error' : ''"
    :help="hasError ? 'Por favor selecciona una variable' : ''"
  >
    <template #label>
      <span class="mrg-right-4"> Guarda la respuesta en una variable </span>

      <a-tooltip title="Las respuestas del contacto se guardarán en el historial del ticket.">
        <InfoCircleOutlined class="mrg-right-8" />
      </a-tooltip>

      <a-switch
        class="form__switch"
        v-model:checked="formState.checked"
        size="small"
        @change="handleChangeChecked"
        :disabled="props.disabled"
      />
    </template>

    <a-select
     :getPopupContainer="(triggerNode) => triggerNode.parentNode"
      v-model:value="formState.variable"
      placeholder="Selecciona una de las variables"
      :disabled="!formState.checked || props.disabled"
      show-search
      @change="handleChangeVar"
      @search="handleSearchVar"
      :loading="loadingFetch"
    >
      <a-select-option
        v-for="varManage in varsManageFormatted"
        :key="varManage.id"
        :disabled="
          varManage.deleted ||
          (varId && varId !== varManage.id && varManage.disabled) ||
          (!varId && varManage.disabled)
        "
      >
        {{ varManage.name }}
        <em v-if="varManage.deleted">(Variable eliminada)</em>
        <em v-else-if="varId && varId === varManage.id ? false : varManage.is_used"
          >(Variable usada en otro nodo)</em
        >
      </a-select-option>
    </a-select>
    <template #extra>
      <p class="text--left mrg-bottom-0">
        Puedes crear tus propias variables.
        <a @click="modalVar.visible = true">Click aquí.</a>
      </p>
    </template>
  </a-form-item>
  <modal-manage-var v-bind="modalVar" @onClose="handleCloseModal" />
</template>

<script setup lang="ts">
import { reactive, inject, computed, Ref, watch, ref } from "vue";
import { VarItemSimple, FunctionToChatbot } from "@/@types/manageVars";
import { assignVarInNode, deleteVarInNode } from "@/app/chatbots/services";
import { message } from "ant-design-vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { ChatbotDetails } from "@/@types/chatbots";
import { fetchVars } from "@/app/manageVars/services";
import ModalManageVar from "@/app/manageVars/components/modals/ModalManageVar.vue";
import { FormVar, PropsModalVar } from "@/@types/manageVars";
import { createVar } from "@/app/manageVars/services";

const props = defineProps<{
  nodeId: number;
  chatbotId: number;
  defaultVar?: VarItemSimple;
  checked?: boolean;
  disabled?: boolean;
}>();

const emits = defineEmits(["onChange", "onChecked", "onChangeVar"]);

const varId = computed<number | undefined>(() => {
  if (!props.defaultVar) return undefined;
  return props.defaultVar && props.defaultVar.id ? props.defaultVar.id : undefined;
});

const formState = reactive({
  variable: varId.value,
  checked: varId.value || props.checked ? true : false,
});
const loadingFetch = ref(false);
const hasError = ref(false);

const validateParams = () => {
  if (formState.checked && !formState.variable) {
    hasError.value = true;
    return false;
  }
  hasError.value = false;
  return true;
};

watch(
  () => formState.variable,
  (newVal) => {
    if (newVal) hasError.value = false;
  }
);

watch(
  () => formState.checked,
  (newVal) => {
    if (!newVal) hasError.value = false;
  }
);

const addVarManage = async (newVar: FormVar) => {
  const response = await createVar(newVar);
  if (response.success) {
    functionsToChatbot.onAddVarManage(response.data);
    handleCloseModal();
    message.success("Se agregó una nueva variable");
  }
};

const modalVar: PropsModalVar = reactive({
  visible: false,
  title: "Crear nueva variable",
  okText: "Crear variable",
  onOk: addVarManage,
});

const varsManage = inject("varsManage") as Ref<VarItemSimple[]>;
const functionsToChatbot = inject("functionsToChatbot") as FunctionToChatbot;
const chatbotDetails = inject("chatbotDetails") as Ref<ChatbotDetails>;

watch(varId, () => {
  formState.variable = varId.value;
  formState.checked = varId.value ? true : false;
});
watch(
  () => props.checked,
  () => {
    formState.checked = !!props.checked;
  }
);

const varsInChatbot = computed(() => {
  return chatbotDetails.value.variables || [];
});

const varsManageFormatted = computed(() => {
  let dataVars = [];
  let existVarDefault = false;
  if (varId.value)
    existVarDefault = varsManage.value.find((item) => item.id === varId.value) ? true : false;

  // para las variables que han sido eliminadas
  if (!existVarDefault && props.defaultVar)
    dataVars = [...varsManage.value, { ...props.defaultVar, disabled: true, deleted: true }];
  else dataVars = varsManage.value;

  if (dataVars) {
    return dataVars.map((item) => {
      const isUsedInAnotherNode =
        !!varsInChatbot.value.length &&
        varsInChatbot.value.some((varInChatbot) => varInChatbot.id === item.id);
      if (isUsedInAnotherNode) {
        item.is_used = true;
        item.disabled = true;
      } else {
        item.is_used = false;
        item.disabled = false;
      }
      return item;
    });
  } else return [];
});

const handleChangeChecked = async (checked: boolean) => {
  if (props.disabled) return;
  if (!checked) formState.variable = undefined;
  emits("onChecked", checked);
};
const unAssignedVar = async () => {
  if (!props.defaultVar) return;
  await deleteVarInNode(props.defaultVar.id_in_node);
  functionsToChatbot.onDeleteVar(props.defaultVar.id);
};

const handleChangeVar = async () => {
  console.log("handleChangeVar");
  emits("onChangeVar");
};

const handleSearchVar = async (val: string) => {
  if (!val) return;
  loadingFetch.value = true;
  await fetchVars({ pagination: { current: 1 }, search: val });
  loadingFetch.value = false;
};

const saveVarInNode = async () => {
  // si se apago el switch y además habia una variable por defecto, entonces desasignará la variable
  if (!formState.checked && props.defaultVar && varId && varId.value) {
    await unAssignedVar();
    functionsToChatbot.onDeleteVar(varId.value);
    message.success("Se actualizó tu variable");
    formState.variable = undefined;
    emits("onChange");
  }
  // si el switch está prendido y se ha seleccionado una variable distinta
  // desasignará la anterior y volverá a asignar la nueva
  else if (formState.checked && formState.variable) {
    if (props.defaultVar && varId && varId.value !== formState.variable) await unAssignedVar();
    await assignVarInNode({
      chatbotId: props.chatbotId,
      nodeId: props.nodeId,
      varId: formState.variable,
    });
    const newVar = varsManageFormatted.value.find(
      (varManage) => varManage.id === formState.variable
    );
    if (newVar) functionsToChatbot.onAddVar(newVar);
    message.success("Se actualizó tu variable");
    emits("onChange");
  }
};

const resetChanges = () => {
  formState.variable = varId.value;
  formState.checked = varId.value ? true : false;
  hasError.value = false;
};

const handleCloseModal = () => {
  modalVar.visible = false;
};

defineExpose({ saveVarInNode, resetChanges, validateParams, varsManageFormatted, formState });
</script>
<style lang="sass" scoped>
.picker
  margin-top: 20px
</style>
